"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/** A horizontal track with no visible scrollbar and arrows over the cards.
 *
 *  The scrollbar is hidden rather than removed: the element still scrolls, so
 *  a trackpad swipe, a touch drag, shift+wheel and keyboard scrolling all keep
 *  working. Only the chrome goes. Hiding it needs both the standard
 *  `scrollbar-width` and the WebKit pseudo-element, and the pseudo-element
 *  cannot be expressed as an inline style — hence the tiny styled-jsx block.
 *
 *  Arrows sit vertically centred over the cards and disable at each end, so
 *  there is always a visible cue that there is more to the side. They are
 *  hidden entirely when the content fits, which is why `ends` is measured
 *  rather than assumed.
 *
 *  `autoScroll` advances one card at a time and stops for good on the first
 *  real interaction — an auto-scroller that fights the reader is worse than
 *  none. It also stays put while off-screen or when the tab is hidden, and
 *  never starts at all under prefers-reduced-motion.
 */
export function ArrowScroller({
  children,
  className = "",
  autoScroll = false,
  interval = 3500,
  label = "Scroll cards",
}: {
  children: React.ReactNode;
  className?: string;
  autoScroll?: boolean;
  interval?: number;
  label?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [ends, setEnds] = useState({ start: true, end: true });
  const stopped = useRef(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // A 2px tolerance — fractional widths mean scrollLeft rarely lands on 0
    // or on `max` exactly, which would leave an arrow permanently enabled.
    setEnds({ start: el.scrollLeft <= 2, end: el.scrollLeft >= max - 2 });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
    };
  }, [measure]);

  /** One card's worth, falling back to most of the viewport. */
  const step = useCallback((dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>(":scope > * > *");
    const by = card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * by, behavior: "smooth" });
  }, []);

  const nudge = useCallback((dir: 1 | -1) => {
    stopped.current = true;
    step(dir);
  }, [step]);

  useEffect(() => {
    if (!autoScroll) return;
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let visible = false;
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0.3 });
    io.observe(el);

    // Any deliberate interaction ends the auto-scroll permanently. "scroll"
    // is NOT in this list: the auto-scroll fires scroll events itself, so
    // listening for it would stop the timer on its own first tick.
    const stop = () => { stopped.current = true; };
    const events = ["pointerdown", "wheel", "touchstart", "keydown"] as const;
    events.forEach((e) => el.addEventListener(e, stop, { passive: true }));

    const id = setInterval(() => {
      if (stopped.current || !visible || document.hidden) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      if (el.scrollLeft >= max - 2) el.scrollTo({ left: 0, behavior: "smooth" });
      else step(1);
    }, interval);

    return () => {
      clearInterval(id);
      io.disconnect();
      events.forEach((e) => el.removeEventListener(e, stop));
    };
  }, [autoScroll, interval, step]);

  const atBothEnds = ends.start && ends.end;

  return (
    <div className="relative">
      <div ref={ref} className={`gcc-noscrollbar overflow-x-auto ${className}`}>
        {children}
      </div>

      {!atBothEnds && (
        <>
          <Arrow side="left" disabled={ends.start} onClick={() => nudge(-1)} label={`${label} — previous`} />
          <Arrow side="right" disabled={ends.end} onClick={() => nudge(1)} label={`${label} — next`} />
        </>
      )}

      <style jsx>{`
        .gcc-noscrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .gcc-noscrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}

function Arrow({
  side, disabled, onClick, label,
}: {
  side: "left" | "right";
  disabled: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`absolute top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-brand-line bg-white/95 text-brand-accent shadow-[0_2px_10px_rgba(0,0,0,0.10)] backdrop-blur-sm transition hover:border-brand-accent hover:bg-brand-accent hover:text-white disabled:pointer-events-none disabled:opacity-0 ${
        side === "left" ? "left-2 sm:left-3" : "right-2 sm:right-3"
      }`}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d={side === "left" ? "M15 5 8 12l7 7" : "M9 5l7 7-7 7"}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
